import { SbbLoadingIndicator } from "@sbb-esta/lyne-react/loading-indicator";
import { useQuery } from "@tanstack/react-query";
import { ErrorCode } from "components/ErrorCode";
import { AppEnvironmentEnum } from "enums/AppEnvironmentEnum";
import { BoardRoleEnum } from "enums/BoardRoleEnum";
import { isCancelled } from "helpers";
import { createLogger } from "logging";
import { environment } from "models/Environment";
import { Permissions } from "models/Permissions";
import { useApi } from "providers/Api";
import { useTranslations } from "providers/Translations";
import { PropsWithChildren, useCallback, useMemo, useState } from "react";

import { PermissionsContext } from "./PermissionsContext";

const logger = createLogger("PermissionsProvider");

const calcIsAdministrator = (permissions: Permissions | undefined): boolean => permissions?.isAdministrator || false;

const calcIsBoardOwner = (permissions: Permissions | undefined): boolean =>
  (permissions?.boardRoles || []).some((x) => x.role === BoardRoleEnum.Owner);

const calcIsBoardMember = (permissions: Permissions | undefined): boolean =>
  (permissions?.boardRoles || []).some((x) => x.role === BoardRoleEnum.Member);

export const PermissionsProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { axios } = useApi();
  const i18n = useTranslations();
  const [permissions, setPermissions] = useState<Permissions>();
  const [errorCode, setErrorCode] = useState(0);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleHttpError = useCallback((error?: any) => {
    if (isCancelled(error)) {
      logger.debug(`handleHttpError(): is cancel, ignoring`, error);
      return;
    }
    const code = error?.response?.status || 502;
    logger.debug(`handleHttpError(): code`, code);
    setErrorCode(code);
  }, []);

  const { isLoading: queryLoading } = useQuery({
    queryKey: ["permissions", i18n.currentLanguage],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      try {
        const result = await axios.get<Permissions>(`permissions`, { signal });
        setPermissions(result.data);

        if (
          result.data.isAdministrator &&
          (environment.name == AppEnvironmentEnum.Localhost || environment.name == AppEnvironmentEnum.Develop)
        ) {
          const bodyClasses = document.getElementsByTagName("body")[0].classList;
          if (!bodyClasses.contains("is-administrator")) bodyClasses.add("is-administrator");
        }
        return result.data;
      } catch (error) {
        if (!isCancelled(error)) {
          handleHttpError(error);
        }
        throw error;
      }
    },
    enabled: !!i18n.currentLanguage,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    retry: 5,
  });

  const isError = !!errorCode;
  const isLoading = queryLoading && !errorCode;
  const isSuccess = !queryLoading && !errorCode;

  const isAdministrator = calcIsAdministrator(permissions);

  const contextValue = useMemo(
    () => ({
      permissions,
      currentUserEmail: `${permissions?.user?.email}`.toLowerCase(),
      isAdministrator,
      isBoardOwner: isAdministrator || calcIsBoardOwner(permissions),
      isBoardMember: isAdministrator || calcIsBoardMember(permissions),
      handleHttpError,
      setErrorCode,
    }),
    [permissions, isAdministrator, handleHttpError, setErrorCode]
  );

  return (
    <PermissionsContext.Provider value={contextValue}>
      {isError && <ErrorCode errorCode={errorCode} />}
      {isLoading && <SbbLoadingIndicator size="xxl" />}
      {isSuccess && children}
    </PermissionsContext.Provider>
  );
};
